import { useState, useCallback } from 'react';
import { KonvaEventObject } from 'konva/lib/Node';
import { MODE_TO_TYPE, isFourPointType } from '../../utils/polygonTypes';
import { gutterPx } from '../../utils/canvasGutter';

const DRAWING_MODES = Object.keys(MODE_TO_TYPE).filter(m => m !== 'create_shelf');

interface UsePolygonCreationReturn {
  tempPoints: [number, number][];
  saving: boolean;
  editFormOpen: boolean;
  selectedRackForShelf: string | null;
  awaitingRackSelection: boolean;
  handleStageClick: (e: KonvaEventObject<MouseEvent>, mode: string, frame: { width: number; height: number } | null, image: HTMLImageElement | undefined, scale: number) => void;
  handleRackClickForShelf: (rackId: string, mode: string) => void;
  handleCancelPolygon: () => void;
  handleCancelShelf: () => void;
  setEditFormOpen: (open: boolean) => void;
  setTempPoints: React.Dispatch<React.SetStateAction<[number, number][]>>;
  setSelectedRackForShelf: (rackId: string | null) => void;
  setAwaitingRackSelection: (awaiting: boolean) => void;
}

export const usePolygonCreation = (): UsePolygonCreationReturn => {
  const [tempPoints, setTempPoints] = useState<[number, number][]>([]);
  const [saving] = useState(false);
  const [editFormOpen, setEditFormOpen] = useState(false);
  const [selectedRackForShelf, setSelectedRackForShelf] = useState<string | null>(null);
  const [awaitingRackSelection, setAwaitingRackSelection] = useState(false);

  const handleStageClick = useCallback((
    e: KonvaEventObject<MouseEvent>,
    mode: string,
    frame: { width: number; height: number } | null,
    image: HTMLImageElement | undefined,
    scale: number
  ) => {
    const isDrawingMode =
      (DRAWING_MODES.includes(mode) && frame) ||
      (mode === 'create_shelf' && frame && !awaitingRackSelection && selectedRackForShelf != null);

    if (!isDrawingMode || !image) return;

    const stage = e.target.getStage();
    if (!stage) return;
    const pointer = stage.getPointerPosition();
    if (!pointer) return;

    const g = gutterPx({ width: image.width, height: image.height });
    const x = pointer.x / scale - g.x;
    const y = pointer.y / scale - g.y;

    // Check if clicking near an existing temp point → remove it
    const HIT_RADIUS = 8 / scale; // 8 pixels tolerance
    const existingIdx = tempPoints.findIndex(([px, py]) => {
      const dx = px - x;
      const dy = py - y;
      return Math.sqrt(dx * dx + dy * dy) < HIT_RADIUS;
    });

    if (existingIdx >= 0) {
      setTempPoints(prev => prev.filter((_, i) => i !== existingIdx));
      return;
    }

    // Enforce 4-point limit for rack, shelf, table
    const entityType = MODE_TO_TYPE[mode];
    if (entityType && isFourPointType(entityType) && tempPoints.length >= 4) {
      return; // Already at max points
    }

    setTempPoints(prev => [...prev, [x, y]]);
  }, [awaitingRackSelection, selectedRackForShelf, tempPoints]);

  const handleRackClickForShelf = useCallback((rackId: string, mode: string) => {
    if (mode === 'create_shelf' && awaitingRackSelection) {
      setSelectedRackForShelf(rackId);
      setAwaitingRackSelection(false);
      setTempPoints([]);
    }
  }, [awaitingRackSelection]);

  const handleCancelPolygon = useCallback(() => {
    setTempPoints([]);
  }, []);

  const handleCancelShelf = useCallback(() => {
    setTempPoints([]);
    setEditFormOpen(false);
    setSelectedRackForShelf(null);
    setAwaitingRackSelection(true);
  }, []);

  return {
    tempPoints,
    saving,
    editFormOpen,
    selectedRackForShelf,
    awaitingRackSelection,
    handleStageClick,
    handleRackClickForShelf,
    handleCancelPolygon,
    handleCancelShelf,
    setEditFormOpen,
    setTempPoints,
    setSelectedRackForShelf,
    setAwaitingRackSelection,
  };
};
